import { useId } from "react";

import { useImageUpload } from "../../hooks/ImageUpload";
import { type CropMode, type ImageCropResult } from "../ImageCropModal";

export type CropProps = {
  size: {
    width: number;
    height: number;
  };
  slice: {
    aspect: number;
  };
  cropShape: "round" | "rect";
  cropMode?: CropMode;
};

type Props = {
  accept: string;
  siteId: string;
  children: React.ReactNode;
  crop?: CropProps;
  onUpload: (value: string, cropResult?: ImageCropResult) => void;
  /** The id of the file input, for other controls to open it (a label's htmlFor, or a click on it). */
  inputId?: string;
  /**
   * The caller shows the upload in place (the screen stays usable): told when the file starts going up,
   * after the crop, and when it fails (without it, a failure is thrown).
   */
  onUploadStart?: () => void;
  onUploadError?: (error: unknown) => void;
};

export const UploadImageInput = ({
  accept,
  siteId,
  children,
  crop,
  onUpload,
  inputId: givenInputId,
  onUploadStart,
  onUploadError,
}: Props) => {
  const { upload, isUploading, cropModal } = useImageUpload({ siteId, crop, onUpload, onUploadStart, onUploadError });
  // One id per instance: with a fixed id, every label on the page opened the first file input
  const ownInputId = useId();
  const inputId = givenInputId || ownInputId;

  const onChangeHandler = (files: FileList) => {
    if (files && files.length) {
      upload(files[0]);
    }
  };

  const resetInputValue = (el: HTMLInputElement) => (el.value = "");

  return (
    <>
      <label htmlFor={inputId}>
        <input
          type="file"
          id={inputId}
          name="upload"
          style={{ display: "none" }}
          accept={accept}
          disabled={isUploading}
          onChange={e => onChangeHandler(e.target.files as FileList)}
          onClick={e => resetInputValue(e.target as HTMLInputElement)}
        />
        {children}
      </label>
      {cropModal}
    </>
  );
};
